import { type PicoXConfiguration } from '../models/configuration.js';
import { type StoredEvent } from '../models/event.js';
import { isPicoXClientError, isPicoXSerializationError } from './exceptions.js';
import { type Network } from './network.js';
import { type Persistence } from './persistence.js';
import { LockId } from './utils/lock-runner.js';
import Mutex from './utils/mutex.js';
import { getDelayWithExponentialBackoff } from './utils/retry.js';

/**
 * Scheduler is a class that schedules sync operations.
 *
 * It's main responsibility is to retrieve events from storage and send them to Pico
 * in a recurring fashion. Every cycle, the Scheduler will:
 * - retrieve events from storage
 * - if any, send events in batch to PicoX
 * - sleep for an amount of time
 *
 * The Scheduler implements a retry logic with exponential backoff for errors with the following logic.
 * We define four types of errors:
 * - clientError: 4xx status codes from the server
 * - serverError: 5xx status codes from the server
 * - serializationError: errors related to the serialization of the events
 * - any other error
 *
 * The retry logic is the following:
 * - clientError: not retried
 * - serverError: retried with exponential backoff
 * - serializationError: not retried
 * - any other error: retried with exponential backoff
 */
export type Scheduler = {
  /** PicoX Configuration */
  configuration: PicoXConfiguration;

  /** Starts scheduling sync operations */
  startScheduling: () => Promise<void>;

  /** Stops scheduling sync operations */
  stopScheduling: () => void;
};

export type TimestampCorrection = {
  delta: number;
  last_event_timestamp: string | null;
};

/**
 * Creates a scheduler for syncing events with the server
 * @param persistence Persistence manager for storing events
 * @param network Network manager for sending events
 * @param configuration PicoX configuration
 * @returns A scheduler instance
 */
export const createScheduler = (
  persistence: Persistence,
  network: Network,
  configuration: PicoXConfiguration,
): Scheduler => {
  // Whether the scheduler is running
  let isRunning = false;

  // Current attempt for the sync operation
  let currentAttempt = 0;

  // Task for the current sync operation
  let schedulerTimeout: number | null = null;

  // The next delay to use for sync operations
  let nextDelay = configuration.syncIntervalMilliseconds;

  // Timestamp correction information
  let timestampCorrection: TimestampCorrection = {
    delta: 0,
    last_event_timestamp: null,
  };

  // Mutex to ensure only one sync operation runs at a time
  const syncEventsMutex = new Mutex();

  /**
   * Syncs events with the server
   */
  const syncEvents = async () => {
    let events: StoredEvent[] = [];

    // This mutex is needed to sync calls to `syncEvents` within the same tab.
    // Ensures only one sync operation runs at a time, preventing concurrent operations
    // that could lead to sending the same events multiple times.
    return syncEventsMutex.run(async () => {
      currentAttempt++;
      try {
        events = await persistence.retrieveEvents(configuration.eventsBatchSize);

        // If there are no events, we should stop and schedule the next sync operation
        if (!events || events.length === 0) {
          currentAttempt = 0;
          nextDelay = configuration.syncIntervalMilliseconds;
          return;
        }

        // Send the events to the server
        const newTimestampCorrection = await network.sendEvents(events, timestampCorrection);

        // clean up and prepare for the next sync operation
        return await onSuccess(newTimestampCorrection, events);
      } catch (error) {
        configuration.logger.error(`Event sync operation failed: ${error}`);

        // If the error is a client error, the events are invalid and there's no point in retrying
        if (isPicoXClientError(error) || isPicoXSerializationError(error)) {
          return await onSuccess(timestampCorrection, events);
        }

        nextDelay = getDelayWithExponentialBackoff({
          baseInterval: configuration.retryBaseInterval,
          currentAttempt,
          maxInterval: configuration.maxExponentialBackoffInterval,
        });
        return;
      } finally {
        scheduleNextSync();
      }
    });
  };

  /**
   * Cleans up and prepares for the next sync operation
   */
  const onSuccess = async (newTimestampCorrection: TimestampCorrection, events: StoredEvent[]) => {
    // Remove events if successfully sent
    await persistence.removeEvents(events);

    currentAttempt = 0;
    timestampCorrection = newTimestampCorrection;
    nextDelay = configuration.syncIntervalMilliseconds;

    // Configured in no-batch mode but there are remaining pending events, schedule another send immediately.
    // This can happen after coming online if many events have been added in the meantime.
    if (configuration.syncIntervalMilliseconds === 0 && events.length === configuration.eventsBatchSize) {
      nextDelay = 1;
    }
  };

  /**
   * Schedules the next sync operation
   */
  const scheduleNextSync = () => {
    if (isRunning) {
      if (typeof window === 'undefined') {
        throw new Error('Scheduler timeout not supported in this environment');
      }
      schedulerTimeout = window.setTimeout(syncEvents, nextDelay);
    }
  };

  return {
    configuration,
    startScheduling: () => {
      if (isRunning) return Promise.resolve();
      isRunning = true;
      // Start the loop that sends events in this tab if and only if there is no other tab sending events.
      // If other tabs have the lock this request will return an (ignored) promise that will run the
      // `sendNextBatch` function only when the lock is available.
      return configuration.webLockApiRunner(LockId.picoXSendEvents, syncEvents);
    },
    stopScheduling: () => {
      isRunning = false;
      if (schedulerTimeout) {
        window.clearTimeout(schedulerTimeout);
        schedulerTimeout = null;
      }
      nextDelay = configuration.syncIntervalMilliseconds;
    },
  };
};
